import React, { useState, useContext } from 'react';

import CloseOutlined from '@ant-design/icons/CloseOutlined';
import { yupResolver } from '@hookform/resolvers/yup';
import { DialogActions } from '@material-ui/core';
import { Grid, TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider, DesktopTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button, Typography } from 'antd';
import { CustomButtonAntd } from 'components/Shared/CustomButtonAntd/style';
import ptBR from 'date-fns/locale/pt-BR';
import { DateDetail } from 'interfaces/event';
import { useForm, Controller, FieldArrayMethodProps } from 'react-hook-form';
import { v4 } from 'uuid';

import FormContext from '../../../../Context';
import { schemaEventDates } from './scheme';
import * as S from './styles';

interface EventDateModalProps {
  appendEventDates: (value: Partial<DateDetail> | Partial<DateDetail>[], options?: FieldArrayMethodProps) => void;
  eventDates: DateDetail[];
  editValues?: DateDetail;
  onClose: () => void;
  open: boolean;
  updateEventDates: (index: number, value: Partial<DateDetail>) => void;
  openTicketsPanel: () => void;
}

export type DateDetailForm = DateDetail & Pick<EventDateModalProps, 'eventDates'>;

const EventDateModal = ({
  appendEventDates,
  eventDates,
  editValues,
  onClose,
  open,
  updateEventDates,
  openTicketsPanel
}: EventDateModalProps) => {
  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm<DateDetailForm>({
    defaultValues: {
      eventDates: [...eventDates],
      ...editValues
    },
    resolver: yupResolver(schemaEventDates),
    mode: 'onChange'
  });

  const { isCreate } = useContext(FormContext);

  const [dateExeption, setDateExeption] = useState(false);

  const dateError = (): boolean => {
    if (errors?.end_time?.type === 'Conflito de datas' || errors?.date) {
      return true;
    }

    if (dateExeption === true) {
      return true;
    }

    return false;
  };

  const dateErrorMessage = (): string => {
    if (errors?.end_time?.type === 'Conflito de datas') {
      return '';
    }

    return errors?.date?.message;
  };

  const startTimeError = (): boolean => {
    if (errors?.end_time?.type === 'Conflito de datas' || errors?.start_time) {
      return true;
    }

    return false;
  };

  const startTimeErrorMessage = (): string => {
    if (errors?.end_time?.type === 'Conflito de datas') {
      return '';
    }

    return errors?.start_time?.message;
  };

  const endTimeError = (): boolean => {
    if (errors?.end_time?.type === 'Conflito de datas' || errors?.end_time) {
      return true;
    }

    return false;
  };

  const endTimeErrorMessage = (): string => {
    if (errors?.end_time?.type === 'Conflito de datas') {
      return '';
    }

    return errors?.end_time?.message;
  };

  const shouldRenderEndTimeError = () => {
    if (errors?.end_time?.type === 'Conflito de datas') {
      return true;
    }

    return false;
  };

  const handleSave = (data: DateDetailForm) => {
    delete data.eventDates;

    const eventDateIndex = eventDates.findIndex(eventDate => eventDate.arrayId === data.arrayId);

    if (!data.date) {
      setDateExeption(true);
      return;
    }

    if (eventDates[eventDateIndex]) {
      updateEventDates(eventDateIndex, data);
      return onClose();
    }

    const values: DateDetail = { ...data, identificator: v4(), arrayId: v4() };
    appendEventDates(values);
    isCreate && openTicketsPanel();
    return onClose();
  };

  return (
    <S.Wrapper>
      <S.CustomizedDialog
        open={open}
        onClose={() => onClose()}
        PaperProps={{
          sx: {
            maxWidth: '650px',
            width: '650px'
          }
        }}
      >
        <S.CustomizedDialogTitle>
          <Typography.Title level={5}>Adicionar dia do evento</Typography.Title>
          <Button onClick={() => onClose()} style={{ border: 'none' }}>
            <CloseOutlined style={{ color: '#000000' }} />
          </Button>
        </S.CustomizedDialogTitle>
        <S.CustomizedDialogContent>
          <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              <Grid container item spacing={3}>
                <Grid item xs={12} md={12}>
                  <Controller
                    control={control}
                    defaultValue={null}
                    name='date'
                    render={({ field: { onChange, value } }) => (
                      <DesktopDatePicker
                        disablePast
                        inputFormat='dd/MM/yyyy'
                        onChange={onChange}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={dateError()}
                            helperText={dateErrorMessage()}
                            label='Data do evento'
                            fullWidth
                            data-testid='date'
                          />
                        )}
                        value={value}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid container item spacing={3}>
                <Grid item xs={12} md={6}>
                  <Controller
                    control={control}
                    defaultValue={null}
                    name='start_time'
                    render={({ field: { onChange, value } }) => (
                      <DesktopTimePicker
                        onChange={onChange}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={startTimeError()}
                            helperText={startTimeErrorMessage()}
                            label='Horário de início'
                            fullWidth
                            data-testid='start-time'
                          />
                        )}
                        value={value}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    control={control}
                    defaultValue={null}
                    name='end_time'
                    render={({ field: { onChange, value } }) => (
                      <DesktopTimePicker
                        onChange={onChange}
                        renderInput={params => (
                          <TextField
                            {...params}
                            error={endTimeError()}
                            helperText={endTimeErrorMessage()}
                            label='Horário de término'
                            fullWidth
                            data-testid='end-time'
                          />
                        )}
                        value={value}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {shouldRenderEndTimeError() && (
                <Grid alignItems='center' container item spacing={3}>
                  <Grid item xs={1}>
                    <S.CustomizedWarningAmberIcon />
                  </Grid>
                  <Grid item xs>
                    <S.CustomizedTypography error>
                      Conflito de horários, já existe um evento neste horáro, informe um novo horário para salvar.
                    </S.CustomizedTypography>
                  </Grid>
                </Grid>
              )}

              {dateExeption && (
                <Grid alignItems='center' container item spacing={3}>
                  <Grid item xs={1}>
                    <S.CustomizedWarningAmberIcon />
                  </Grid>
                  <Grid item xs>
                    <S.CustomizedTypography error>Data inválida</S.CustomizedTypography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </LocalizationProvider>
        </S.CustomizedDialogContent>
        <DialogActions>
          <Grid container spacing={3}>
            <Grid container item spacing={2} justifyContent='end'>
              <Grid item>
                <CustomButtonAntd onClick={() => onClose()}>Cancelar</CustomButtonAntd>
              </Grid>
              <Grid item>
                <Button onClick={handleSubmit(handleSave)} type='primary'>
                  Confirmar
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </S.CustomizedDialog>
    </S.Wrapper>
  );
};

export default EventDateModal;
